import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, ArrowLeft, Check } from "lucide-react";
import { api } from "../api";
import { toast } from "sonner";

const PwField = ({ value, onChange, placeholder, show, testid }) => (
  <div className="relative">
    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
    <input type={show ? "text" : "password"} required minLength={6} placeholder={placeholder} value={value}
      onChange={(e) => onChange(e.target.value)} className="input-brut !pl-9 !pr-10" data-testid={testid} autoComplete="new-password" />
  </div>
);

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => navigate("/login"), 3000);
    return () => clearTimeout(t);
  }, [done, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) return setErr("Password must be at least 6 characters.");
    if (pw !== confirm) return setErr("Passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/password/reset", { token, password: pw });
      setDone(true);
      toast.success("Password updated");
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Couldn't reset your password. Please request a new link.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-20 pb-32" data-testid="reset-password-page">
      <Link to="/login" className="font-mono-label text-[10px] text-neutral-500 hover:text-white inline-flex items-center gap-1 mb-6"><ArrowLeft size={11} /> BACK TO SIGN IN</Link>
      <div className="font-mono-label text-[10px] text-[var(--accent)] mb-3">PASSWORD RESET</div>
      <h1 className="font-display text-5xl uppercase leading-none mb-8">New Password</h1>

      {!token ? (
        <div className="border border-neutral-800 p-6" data-testid="reset-invalid">
          <p className="text-neutral-300 text-sm mb-4">This reset link is missing or broken. Request a fresh one.</p>
          <Link to="/forgot-password" className="btn-primary" data-testid="reset-request-new">Request new link</Link>
        </div>
      ) : done ? (
        <div className="border border-[var(--accent)] p-6 bg-[#1a0a05]" data-testid="reset-success">
          <div className="w-10 h-10 border border-[var(--accent)] flex items-center justify-center mb-4">
            <Check size={16} className="text-[var(--accent)]" />
          </div>
          <h2 className="font-headline text-xl uppercase mb-2">Password Updated</h2>
          <p className="text-neutral-300 text-sm mb-4">Redirecting you to sign in…</p>
          <Link to="/login" className="btn-ghost" data-testid="reset-go-login">Sign in now</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <PwField value={pw} onChange={setPw} placeholder="New password" show={show} testid="reset-password" />
          <PwField value={confirm} onChange={setConfirm} placeholder="Confirm new password" show={show} testid="reset-confirm" />
          <button type="button" onClick={() => setShow((s) => !s)} className="font-mono-label text-[10px] text-neutral-400 hover:text-[var(--accent)] inline-flex items-center gap-1" data-testid="reset-toggle-visibility">
            {show ? <EyeOff size={12} /> : <Eye size={12} />} {show ? "HIDE PASSWORDS" : "SHOW PASSWORDS"}
          </button>
          {err && (
            <div data-testid="reset-error">
              <p className="text-[var(--accent)] font-mono-label text-xs">{err}</p>
              {/invalid|expired|used/i.test(err) && <Link to="/forgot-password" className="font-mono-label text-[10px] text-neutral-400 underline" data-testid="reset-error-request-new">Request a new link</Link>}
            </div>
          )}
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="reset-submit">
            {busy ? "Updating…" : "Update password"}
          </button>
        </form>
      )}
    </div>
  );
}
